import { ReactElement, useState } from 'react';
import { StatusBar, Modal, PrimaryButton, SecondaryButton, Spinner } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import PublishSetupView from './PublishSetupView';
import PublishView from './PublishView';
import SummaryView from './SummaryView/SummaryView';
import { NoChannelsView } from './NoChannelsView/NoChannelsView';
import { useExternalSystemPublishing } from '../../hooks/useExternalSystemPublishing';
import filterMealtimesWithCategories from './utilities/filterMealtimesWithCategories';
import { getVenuesWithChannels } from './utilities/getVenuesWithChannels/getVenuesWithChannels';
import ModalImg from '../../assets/images/chef-plating.png';
import { IModalProps, IPublishDestination, PublishSteps } from './types';
import styles from './PublishModal.module.scss';
import { FieldValues, useForm } from 'react-hook-form';
import { IPublishMenusProps } from 'src/api/publishMenu/types';
import { errorNotification, successNotification } from './PublishView/Notifications';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useNavigate } from 'react-router-dom';
import { usePublishMenu } from 'src/hooks/usePublishMenu/usePublishMenu';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { formatScheduledTime, publishTimes } from '../PublishDetailsView/shared';
import { IMenu } from 'src/types/menu.types';
import dayjs from 'dayjs';

const steps = [PublishSteps.Setup, PublishSteps.Venues, PublishSteps.Summary];

const PublishFlowWrapper = ({
  menuId,
  isTrial,
  closeModal,
}: {
  menuId: string;
  isTrial: boolean;
  closeModal: () => void;
}) => {
  const { data: menu, isLoading: isMenuLoading } = useMenu(menuId);
  const {
    data: publicationData,
    isLoading: isPublicationDataLoading,
    isError,
  } = useExternalSystemPublishing(isTrial, menu?.publication);

  if (isMenuLoading || isPublicationDataLoading) {
    return (
      <div className={styles['publish-flow']}>
        <Spinner size="large" data-testid="publish-loading" />
      </div>
    );
  }

  if (!publicationData || publicationData.length === 0 || isError) {
    return (
      <div className={styles['publish-flow']}>
        <NoChannelsView />
      </div>
    );
  }

  return (
    <PublishFlow
      menuId={menuId}
      isTrial={isTrial}
      closeModal={closeModal}
      publicationData={publicationData}
      menu={menu}
    />
  );
};

const PublishFlow = ({
  menuId,
  isTrial,
  closeModal,
  publicationData,
  menu,
}: {
  menuId: string;
  isTrial: boolean;
  publicationData: IPublishDestination[];
  menu: IMenu;
  closeModal: () => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { trackEvent, captureException } = useUserTracking();

  const publishMenu = usePublishMenu(menuId);
  const validate = useValidate();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [step, setStep] = useState(PublishSteps.Setup);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showSchedule = !isTrial;

  const defaultSelectedChannels = publicationData.map((channel) => channel.channel_id);
  const defaultSelectedVenues = Array.from(
    new Set(
      publicationData
        .flatMap((channel) => channel.venues)
        .filter((venue) => menu.linkedVenueIds.has(venue.venue_id))
        .map((venue) => venue.venue_id),
    ),
  );

  const defaultValues = {
    'price-scheme': undefined,
    publicationType: publishTimes.NOW,

    publicationDate: '',
    scheduleName: menu.name,
    mealtimes: 'disabled',
    selectedChannels: defaultSelectedChannels,
    venues: defaultSelectedVenues,
    confirmationCheckbox: false,
  };

  const formFields = useForm<FieldValues>({
    defaultValues,
  });
  const { control, watch, register, setValue, handleSubmit } = formFields;

  const filteredMealtimes = filterMealtimesWithCategories(menu.mealtimes);

  const getButtonDisabledState = (values: FieldValues, step: PublishSteps) => {
    switch (step) {
      case PublishSteps.Setup: {
        const hasPriceScheme = values['price-scheme'] !== undefined;
        const hasChannel = (values['selectedChannels'] ?? []).length > 0;
        const scheduleReady =
          values['publicationType'] !== publishTimes.SCHEDULE ||
          (Boolean(values['confirmationCheckbox']) &&
            dayjs(values['publicationDate']).isValid() &&
            dayjs(values['publicationDate']).isAfter(dayjs()));
        return !hasPriceScheme || !hasChannel || (showSchedule && !scheduleReady);
      }
      case PublishSteps.Venues:
        // Count only venues that still belong to a selected channel — a venue can be left checked
        // in the flat map after its channel is de-selected on the setup step.
        return getVenuesWithChannels(publicationData, values['selectedChannels'], values['venues']).length === 0;
      case PublishSteps.Summary:
        return (
          isSubmitting ||
          getVenuesWithChannels(publicationData, values['selectedChannels'], values['venues']).length === 0
        );
      default:
        return false;
    }
  };

  const decrementStep = () => {
    const index = steps.indexOf(step) - 1;
    const prevStep = steps.at(index);
    return setStep(prevStep);
  };

  const incrementStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const index = steps.indexOf(step) + 1;
    const nextStep = steps.at(index);
    return setStep(nextStep);
  };

  const isSummaryStep = step === PublishSteps.Summary;
  const isScheduled = watch('publicationType') === publishTimes.SCHEDULE;
  // Summary and Setup close the modal; only the Venues step steps back.
  const leftButtonIsBack = step === PublishSteps.Venues;
  const leftButtonAction = leftButtonIsBack ? decrementStep : closeModal;

  const channelNameDict = publicationData.map(({ channel_id, name }) => [channel_id, name]);

  const values = watch();
  const continueDisabled = getButtonDisabledState(values, step);
  const selectedChannelIds: string[] = values['selectedChannels'] ?? [];

  const isMealtimeOptionEnabled = values['mealtimes'] === 'enabled';
  const handlePublish = async (values: FieldValues) => {
    clearError();
    setIsSubmitting(true);

    const selectedChannelSet = new Set<string>(values['selectedChannels'] ?? []);
    const selectedVenueIds = new Set((values['venues'] ?? []) as string[]);

    const channels: IPublishMenusProps[] = publicationData
      .filter((channel) => selectedChannelSet.has(channel.channel_id))
      .map((channel) => ({
        channel_id: channel.channel_id,
        venues: channel.venues.map((venue) => venue.venue_id).filter((venueId) => selectedVenueIds.has(venueId)),
      }))
      .filter((channel) => channel.venues.length > 0);

    const selectedChannels = new Set(channels.map((c) => c.channel_id));
    const names = channelNameDict
      .filter(([channel_id]) => selectedChannels.has(channel_id))
      .map((channel) => channel.at(1));

    const publication_date_time = formatScheduledTime(values['publicationType'], dayjs(values['publicationDate']));

    try {
      const response = await publishMenu.mutateAsync({
        price_scheme_id: values['price-scheme'],
        use_mealtimes: values['mealtimes'] === 'enabled',
        ...(publication_date_time && {
          publication_date_time: new Date(publication_date_time).toISOString(),
          schedule_name: values['scheduleName'],
        }),
        channels,
      });

      const isScheduled = values['publicationType'] === publishTimes.SCHEDULE;

      // Validation only applies to scheduled menu publishes - publishing
      // immediately has no schedule to attach a report to. This fires
      // fire-and-forget: the report is viewed later from the schedule card,
      // not blocking the publish success flow here.
      if (isScheduled && response?.schedule_id) {
        validate.mutate({
          criteria: {
            schedule_id: response.schedule_id,
            price_scheme_id: values['price-scheme'],
            menu_id: menuId,
            rules: getValidationRules(EVENT_TYPES.PUBLISH_MENU),
          },
        });
      }

      trackEvent('menu_published', {
        menuId,
        menuName: menu.name,
        channelCount: channels.length,
        useMealtimes: values['mealtimes'] === 'enabled',
        isScheduled,
      });

      showNotification(successNotification(menu.name, names, publication_date_time));
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (isScheduled) {
        closeModal();
      } else {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        navigate(`/menus/${menuId}/status`);
      }
    } catch (error) {
      setIsSubmitting(false);
      captureException(error, { tags: { action: 'publish_menu', menuId } });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return showNotification(errorNotification(menu.name, publication_date_time, errorMsg));
    }
  };

  const primaryLabel = isSummaryStep ? (isScheduled ? 'Schedule publish' : 'Publish now') : 'Continue';

  return (
    <div className={styles['publish-flow']}>
      <main>
        <form onSubmit={handleSubmit(handlePublish)}>
          {step === PublishSteps.Setup && (
            <PublishSetupView
              control={control}
              register={register}
              watch={watch}
              menuName={menu.name}
              mealtimes={menu.mealtimes}
              filteredMealtimes={filteredMealtimes}
              publicationData={publicationData}
              showSchedule={showSchedule}
            />
          )}
          {step === PublishSteps.Venues && (
            <PublishView
              selectedVenueIds={values['venues'] ?? []}
              onVenuesChange={(venueIds) => setValue('venues', venueIds, { shouldDirty: true })}
              publicationData={publicationData}
              selectedChannelIds={selectedChannelIds}
            />
          )}
          {step === PublishSteps.Summary && (
            <SummaryView
              watch={watch}
              menuName={menu.name}
              publicationData={publicationData}
              filteredMealtimes={filteredMealtimes}
              isMealtimeOptionEnabled={isMealtimeOptionEnabled}
            />
          )}
          <div className={styles['buttons-container']}>
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={leftButtonAction} type="button">
              {leftButtonIsBack ? 'Back' : 'Cancel'}
            </SecondaryButton>
            <PrimaryButton
              onClick={isSummaryStep ? undefined : incrementStep}
              type={isSummaryStep ? 'submit' : 'button'}
              size="full-width"
              disabled={continueDisabled}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Publishing' : primaryLabel}
            </PrimaryButton>
          </div>
          <div className={styles['error-banner']}>
            {errorMessage && <StatusBar message={errorMessage} variant="error" />}
          </div>
        </form>
      </main>
    </div>
  );
};

const PublishModal = ({ closeModal, isOpen, menuId, isTrial = false }: IModalProps): ReactElement => {
  return (
    <Modal closeModal={closeModal} open={isOpen} image={ModalImg} imageAlt="Picture of a chef plating up">
      <PublishFlowWrapper menuId={menuId} isTrial={isTrial} closeModal={closeModal} />
    </Modal>
  );
};

export default PublishModal;
